/**
 * SanityRecommendationPanel - Display Sanity recommendation data
 *
 * Shows content, dosing, and patient population details fetched from Sanity CMS
 * Handles loading and error states appropriately
 *
 * Requirements: ES-REQ-013-SANITY, ES-REQ-014-SANITY, ES-REQ-015-SANITY
 */

import React from 'react';
import styled from 'styled-components';
import type { RecommendationSnapshot, SanityFetchError, Antimicrobial } from '@/types/labeling';

interface SanityRecommendationPanelProps {
  recommendation: RecommendationSnapshot | null;
  error: SanityFetchError | null;
  isLoading: boolean;
}

/**
 * Get frequency label matching main app logic from Treatment.tsx
 * Handles injectable forms, weekly dosing, and single doses
 */
function getFrequencyLabel(frequency: number | null | undefined, formTitle?: string): string {
  const form = formTitle?.toLowerCase().trim() || "";
  const isInjectable = form.includes("injection") || form.includes("infusion");

  if (!frequency || frequency <= 0) return "single dose";
  // Handle weekly frequency - using 1.52 as the indicator for weekly
  if (frequency === 1.52) return "once weekly";

  switch (frequency) {
    case 1:
      return isInjectable ? "every 24 hours" : "once daily";
    case 2:
      return isInjectable ? "every 12 hours" : "twice daily";
    case 3:
      return isInjectable ? "every 8 hours" : "three times daily";
    case 4:
      return isInjectable ? "every 6 hours" : "four times daily";
    case 6:
      return isInjectable ? "every 4 hours" : "six times daily";
    default:
      return `${frequency}× daily`; //fallback
  }
}

/**
 * Generate condensed treatment summary matching deliverable 3 spec
 * Format: "omeprazole 40mg twice daily for 14d"
 * Handles single dose (null/0 duration) like main app
 */
function generateTreatmentSummary(antimicrobials?: Antimicrobial[]): string {
  if (!antimicrobials || antimicrobials.length === 0) {
    return '';
  }

  return antimicrobials
    .map(drug => {
      const parts: string[] = [drug.name];

      if (drug.dose && drug.dosageUnit) {
        parts.push(`${drug.dose}${drug.dosageUnit}`);
      }

      if (typeof drug.frequency === 'number') {
        parts.push(getFrequencyLabel(drug.frequency));
      } else if (drug.frequency) {
        parts.push(drug.frequency);
      }

      // Only show duration if it exists AND it's not a single dose (frequency !== 0)
      const isSingleDose = typeof drug.frequency === 'number' && drug.frequency === 0;
      if (drug.duration && !isSingleDose && drug.durationUnit) {
        const unit = drug.durationUnit === 'days' ? 'd' : drug.durationUnit;
        parts.push(`for ${drug.duration}${unit}`);
      }

      return parts.join(' ');
    })
    .join(' +\n');
}

export const SanityRecommendationPanel: React.FC<SanityRecommendationPanelProps> = ({
  recommendation,
  error,
  isLoading
}) => {
  if (isLoading) {
    return (
      <Panel>
        <LoadingMessage>Loading recommendation data...</LoadingMessage>
      </Panel>
    );
  }

  if (error) {
    return (
      <Panel>
        <ErrorMessage>
          {error.type === 'missing'
            ? 'Recommendation not found in Sanity'
            : 'Error loading recommendation data'}
        </ErrorMessage>
        <ErrorDetail>{error.message}</ErrorDetail>
      </Panel>
    );
  }

  if (!recommendation) {
    return null;
  }

  // Generate condensed treatment summary
  const treatmentSummary = generateTreatmentSummary(recommendation.antimicrobials);

  return (
    <Panel>
      <SectionTitle>Recommendation Details</SectionTitle>

      {/* Treatment Summary Section - condensed view */}
      {treatmentSummary && (
        <Section>
          <SectionLabel>Treatment:</SectionLabel>
          <TreatmentSummary>{treatmentSummary}</TreatmentSummary>
        </Section>
      )}

      {/* Recommendation Link */}
      {recommendation._id && (
        <Section>
          <RecommendationLink
            href={`https://tewhatakura.nz/recommendation/${recommendation._id}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            View full recommendation →
          </RecommendationLink>
        </Section>
      )}

      {recommendation.content && (
        <Section>
          <SectionLabel>Content:</SectionLabel>
          <SectionContent>{recommendation.content}</SectionContent>
        </Section>
      )}

      {recommendation.population && (
        <Section>
          <SectionLabel>Patient Population:</SectionLabel>
          <SectionContent>{recommendation.population}</SectionContent>
        </Section>
      )}

      {!recommendation.content && !treatmentSummary && !recommendation.population && (
        <EmptyMessage>No recommendation details available</EmptyMessage>
      )}
    </Panel>
  );
};

// Styled components matching existing patterns
const Panel = styled.div`
  background: var(--bg-panel);
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
`;

const SectionTitle = styled.h3`
  margin: 0 0 16px 0;
  font-size: 18px;
  font-weight: var(--font-weight-semibold);
  color: var(--text-dark);
`;

const Section = styled.div`
  margin-bottom: 16px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionLabel = styled.div`
  font-weight: var(--font-weight-semibold);
  color: var(--text-muted);
  margin-bottom: 4px;
  font-size: var(--font-size-base);
`;

const SectionContent = styled.div`
  color: var(--text-dark);
  line-height: 1.6;
  font-size: var(--font-size-base);
  white-space: pre-wrap;
`;

const LoadingMessage = styled.div`
  color: var(--text-muted);
  font-style: italic;
`;

const ErrorMessage = styled.div`
  color: #d32f2f;
  font-weight: var(--font-weight-semibold);
  margin-bottom: 8px;
`;

const ErrorDetail = styled.div`
  color: var(--text-muted);
  font-size: var(--font-size-base);
`;

const EmptyMessage = styled.div`
  color: #999;
  font-style: italic;
  font-size: var(--font-size-base);
`;

// Styled components for treatment summary and recommendation link
const TreatmentSummary = styled.div`
  color: var(--text-dark);
  line-height: 1.8;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  white-space: pre-line;
  background-color: #f9f9f9;
  padding: 12px;
  border-radius: 4px;
  border-left: 3px solid #2196f3;
`;

const RecommendationLink = styled.a`
  color: #1976d2;
  text-decoration: none;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  display: inline-flex;
  align-items: center;
  gap: 4px;

  &:hover {
    text-decoration: underline;
    color: #1565c0;
  }
`;
